import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { billing } from '@buildbee/domain';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  DataTable,
  Dialog,
  DialogContent,
  ErrorState,
  Field,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Status,
  Textarea,
  formatDate,
  formatRelative,
  segmentedItemClass,
  segmentedListClass,
  useToast,
  type ColumnDef,
} from '@buildbee/ui';
import { BUSINESS_TYPE_LABELS, ROLE_LABELS, type BusinessType, type Role } from '@buildbee/contracts';
import { Wordmark } from '../../shell/Logo';
import { BusinessLocationFields } from '../workspace/auth/pages';
import { statusTone } from '../status-tone';
import { CompanyHeader, PlatformNav } from './nav';
import { authClient } from '../../lib/auth-client';
import { setWorkspaceSlug } from '../../lib/workspace';
import { errorMessage, trpc, type RouterOutputs } from '../../lib/trpc';

/**
 * BuildBee platform console (/admin) for super admins: every company, its plan and billing state,
 * and support actions (comp a plan, extend a trial, suspend). Built only from @buildbee/ui parts.
 */

type WorkspaceRow = RouterOutputs['platform']['workspaces']['list'][number];
type State = WorkspaceRow['state'];

export const STATE_BADGE: Record<State, { label: string; variant: 'info' | 'success' | 'warning' | 'destructive' | 'outline' }> = {
  trialing: { label: 'Trial', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  past_due: { label: 'Payment failed', variant: 'warning' },
  inactive: { label: 'Lapsed', variant: 'outline' },
  suspended: { label: 'Suspended', variant: 'destructive' },
};

/** Subscription amounts are in the plans' currency (CAD), formatted for en-CA. */
const money = (cents: number) => billing.formatPrice({ priceCents: cents, currency: billing.PLANS.basic.currency });

export function PlatformLayout() {
  const me = trpc.platform.me.useQuery();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Same as the app shell's sign-out: drop the cached session too, or /sign-in sees it and sends
  // the admin straight back here.
  const signOut = async () => {
    await authClient.signOut();
    setWorkspaceSlug(null);
    queryClient.clear();
    await navigate({ to: '/sign-in' });
  };
  if (me.isLoading) return <LoadingState className="min-h-dvh" />;
  if (me.data?.platformRole !== 'super_admin') {
    return (
      <div className="mx-auto max-w-md p-8">
        <ErrorState error={new Error('This page is only for BuildBee staff.')} />
        <Button className="mt-4" variant="outline" onClick={() => void navigate({ to: '/' })}>
          Back to BuildBee
        </Button>
      </div>
    );
  }
  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6">
        <Wordmark />
      </div>
      <PageHeader
        title="Platform admin"
        description="Companies and people on BuildBee: plans, billing, support actions and the record of every change."
        actions={
          <Button
            variant="ghost"
            onClick={() => void signOut()}
          >
            Sign out
          </Button>
        }
      />
      <PlatformNav />
      <Outlet />
    </div>
  );
}

export function PlatformHomePage() {
  const overview = trpc.platform.overview.useQuery();
  const [state, setState] = useState<State | ''>('');
  const list = trpc.platform.workspaces.list.useQuery(state ? { state } : undefined);
  const navigate = useNavigate();

  const columns: ColumnDef<WorkspaceRow, unknown>[] = [
    {
      id: 'name',
      accessorFn: (w) => `${w.name} ${w.slug} ${w.ownerEmail ?? ''}`,
      header: 'Company',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.name}</div>
          <div className="text-xs text-muted-foreground">{row.original.ownerEmail ?? 'no owner'}</div>
        </div>
      ),
    },
    { id: 'plan', accessorKey: 'planName', header: 'Plan' },
    {
      id: 'billing',
      accessorFn: (w) => `${w.billingMode} ${w.hiveOrgId ?? ''}`,
      header: 'Billing',
      cell: ({ row }) =>
        row.original.billingMode === 'hive' ? (
          <div>
            <div>Hive</div>
            <div className="figures text-xs text-muted-foreground">{row.original.hiveOrgId}</div>
          </div>
        ) : (
          <span className="text-muted-foreground">Standalone</span>
        ),
    },
    {
      id: 'state',
      accessorKey: 'state',
      header: 'Status',
      cell: ({ row }) => {
        const b = STATE_BADGE[row.original.state];
        const days = row.original.state === 'trialing' && row.original.trialDaysLeft !== null ? ` · ${row.original.trialDaysLeft}d left` : '';
        return <Status tone={statusTone(row.original.state)}>{`${b.label}${days}`}</Status>;
      },
    },
    { id: 'members', accessorKey: 'members', header: 'Users' },
    { id: 'projects', accessorKey: 'projects', header: 'Projects' },
    { id: 'createdAt', accessorKey: 'createdAt', header: 'Signed up', cell: ({ row }) => <span className="figures">{formatDate(row.original.createdAt)}</span> },
  ];

  return (
    <div className="space-y-6">
      {overview.data && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat title="Companies" value={overview.data.workspaces.toLocaleString()} />
          <Stat title="Paying MRR" value={money(overview.data.mrrCents)} />
          <Stat title="On trial" value={String(overview.data.byState.trialing)} />
          <Stat title="Need attention" value={String(overview.data.byState.past_due + overview.data.byState.suspended + overview.data.byState.inactive)} />
        </div>
      )}
      <DataTable
        columns={columns}
        data={list.data}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        searchPlaceholder="Search companies or owner email"
        initialSorting={[{ id: 'createdAt', desc: true }]}
        onRowClick={(w) => void navigate({ to: '/admin/workspaces/$workspaceId', params: { workspaceId: w.id } })}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <div role="group" aria-label="Status" className={segmentedListClass}>
              {(['', ...Object.keys(STATE_BADGE)] as (State | '')[]).map((s) => (
                <button key={s || 'all'} type="button" aria-pressed={state === s} className={segmentedItemClass(state === s)} onClick={() => setState(s)}>
                  {s ? STATE_BADGE[s].label : 'All'}
                </button>
              ))}
            </div>
            <NewCompanyButton />
          </div>
        }
        empty={{ title: 'No companies match' }}
      />
    </div>
  );
}

/**
 * Another company for an existing owner. Owners can set up only one company themselves, so a
 * second one (billed on its own plan) is created here.
 */
function NewCompanyButton() {
  const toast = useToast();
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [name, setName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [businessType, setBusinessType] = useState<BusinessType>('commercial');
  const [location, setLocation] = useState<{ country: 'CA' | 'US'; region: string }>({ country: 'CA', region: 'ON' });
  const [plan, setPlan] = useState<'trial' | 'basic' | 'premium'>('trial');
  const [days, setDays] = useState(billing.TRIAL_DAYS);
  const [note, setNote] = useState('');
  const create = trpc.platform.workspaces.create.useMutation({
    onSuccess: (w) => {
      toast.success(`${w.name} created`);
      setOpen(false);
      void utils.platform.invalidate();
      void navigate({ to: '/admin/workspaces/$workspaceId', params: { workspaceId: w.id } });
    },
    onError: (err) => toast.error('Could not create the company', errorMessage(err)),
  });

  return (
    <>
      <Button onClick={() => setOpen(true)}>New company</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title="New company" description="For an owner who already has a BuildBee account. Owners can set up only one company themselves; each extra company has its own plan.">
          <div className="space-y-4">
            <Field label="Owner's email" htmlFor="nc-owner" required>
              <Input id="nc-owner" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@company.com" />
            </Field>
            <Field label="Company name" htmlFor="nc-name" required>
              <Input id="nc-name" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Company code" htmlFor="nc-code" description="Typed on the sign-in page. Leave blank to make one from the name.">
              <Input id="nc-code" value={companyCode} maxLength={12} className="uppercase" placeholder="e.g. VANTOM" onChange={(e) => setCompanyCode(e.target.value.toUpperCase())} />
            </Field>
            <Field label="Business type" htmlFor="nc-type">
              <Select
                id="nc-type"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value as BusinessType)}
                options={(Object.keys(BUSINESS_TYPE_LABELS) as BusinessType[]).map((t) => ({ value: t, label: BUSINESS_TYPE_LABELS[t] }))}
              />
            </Field>
            <BusinessLocationFields country={location.country} region={location.region} onChange={setLocation} />
            <Field label="Plan" htmlFor="nc-plan">
              <Select
                id="nc-plan"
                value={plan}
                onChange={(e) => setPlan(e.target.value as typeof plan)}
                options={[
                  { value: 'trial', label: 'Trial (the owner subscribes from Settings > Billing)' },
                  { value: 'premium', label: `Premium (comped, normally ${money(billing.PLANS.premium.priceCents)}/mo)` },
                  { value: 'basic', label: `Basic (comped, normally ${money(billing.PLANS.basic.priceCents)}/mo)` },
                ]}
              />
            </Field>
            {plan === 'trial' && <DaysField days={days} onChange={setDays} />}
            <Field label="Note" htmlFor="nc-note" description="Why, for the audit log.">
              <Textarea id="nc-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
            </Field>
            <Button
              disabled={!ownerEmail.trim() || !name.trim() || (companyCode !== '' && !/^[A-Z0-9]{3,12}$/.test(companyCode))}
              loading={create.isPending}
              onClick={() =>
                create.mutate({
                  ownerEmail: ownerEmail.trim(),
                  ...(companyCode ? { companyCode } : {}),
                  workspace: { name: name.trim(), businessType, country: location.country, ...(location.region ? { region: location.region } : {}) },
                  plan,
                  trialDays: days,
                  note: note.trim() || undefined,
                })
              }
            >
              Create company
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{title}</CardDescription>
        <CardTitle className="figures">{value}</CardTitle>
      </CardHeader>
    </Card>
  );
}

export function PlatformWorkspacePage({ workspaceId }: { workspaceId: string }) {
  const query = trpc.platform.workspaces.get.useQuery({ id: workspaceId });
  if (query.isLoading) return <LoadingState />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const w = query.data;
  const b = STATE_BADGE[w.state];

  return (
    <div className="space-y-6">
      <CompanyHeader workspaceId={w.id} current="overview" name={w.name} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {w.name} <Status tone={statusTone(w.state)}>{b.label}</Status>
          </CardTitle>
          <CardDescription>
            <span className="figures">/c/{w.slug}</span> · {BUSINESS_TYPE_LABELS[w.businessType as BusinessType] ?? w.businessType} · signed up <span className="figures">{formatDate(w.createdAt)}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {w.suspendedAt && <Alert variant="destructive">Suspended {formatRelative(w.suspendedAt)}: {w.suspendedReason}</Alert>}
          {w.stripeManaged && <Alert variant="info">Billed through Stripe ({w.subscriptionStatus}). Change or cancel the subscription in Stripe; the plan here follows it.</Alert>}
          {w.billingMode === 'hive' && (
            <Alert variant="info">
              Billed through Hive ({w.subscriptionStatus ?? 'no status'}). Plan, trial and status follow Hive&apos;s last push
              {w.hiveSyncedAt ? ` (${formatRelative(w.hiveSyncedAt)})` : ''}; change them in the Hive admin console.
              {w.hiveDeletedAt ? ` Deleted in Hive ${formatRelative(w.hiveDeletedAt)}; purged 30 days later.` : ''}
            </Alert>
          )}
          <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Fact label="Company code" value={w.companyCode ?? '—'} />
            <Fact label="Billing mode" value={w.billingMode === 'hive' ? 'Hive' : 'Standalone'} />
            <Fact label="Hive org id" value={w.hiveOrgId ?? '—'} />
            <Fact label="Plan" value={w.planName} />
            {w.billingMode === 'hive' ? (
              <Fact label={w.state === 'trialing' ? 'Trial ends' : 'Renews'} value={w.state === 'trialing' && w.trialEndsAt ? formatDate(w.trialEndsAt) : 'Billed in Hive'} />
            ) : (
              <Fact label={w.plan === 'trial' ? 'Trial ends' : 'Renews'} value={w.plan === 'trial' ? (w.trialEndsAt ? formatDate(w.trialEndsAt) : '—') : w.currentPeriodEnd ? formatDate(w.currentPeriodEnd) : 'Comped'} />
            )}
            <Fact label="Users" value={String(w.members)} />
            <Fact label="Projects" value={String(w.projects)} />
          </dl>
        </CardContent>
      </Card>
      <SupportActions w={w} />
      <Card>
        <CardHeader>
          <CardTitle>Team</CardTitle>
          <CardDescription>Owners and admins manage this company. BuildBee staff never appear here.</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { id: 'name', accessorKey: 'name', header: 'Name' },
              { id: 'email', accessorKey: 'email', header: 'Email' },
              { id: 'role', accessorKey: 'role', header: 'Role', cell: ({ row }) => ROLE_LABELS[row.original.role as Role] ?? row.original.role },
              { id: 'status', accessorKey: 'status', header: 'Status', cell: ({ row }) => <Status tone={row.original.status === 'active' ? 'done' : 'blocked'}>{row.original.status === 'active' ? 'Active' : 'Suspended'}</Status> },
              { id: 'lastActiveAt', accessorKey: 'lastActiveAt', header: 'Last active', cell: ({ row }) => (row.original.lastActiveAt ? <span className="figures">{formatRelative(row.original.lastActiveAt)}</span> : <span className="text-muted-foreground">Never</span>) },
            ]}
            data={w.memberList}
            searchable={false}
            paginate={false}
          />
        </CardContent>
      </Card>
      {w.platformActions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Support history</CardTitle>
            <CardDescription>Also visible to the company in Settings → Audit log.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {w.platformActions.map((a) => (
                <li key={a.id}>
                  {formatDate(a.createdAt)} · {a.action.replace('platform.', '').replace(/_/g, ' ')}
                  {typeof (a.metadata as { reason?: unknown } | null)?.reason === 'string' ? ` · ${(a.metadata as { reason: string }).reason}` : ''}
                  {typeof (a.metadata as { note?: unknown } | null)?.note === 'string' ? ` · ${(a.metadata as { note: string }).note}` : ''}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

type Detail = RouterOutputs['platform']['workspaces']['get'];

function SupportActions({ w }: { w: Detail }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const [dialog, setDialog] = useState<'plan' | 'trial' | 'suspend' | 'code' | null>(null);
  const done = (message: string) => {
    toast.success(message);
    setDialog(null);
    void utils.platform.invalidate();
  };
  const onError = (err: unknown) => toast.error('Action failed', errorMessage(err));
  const setPlan = trpc.platform.workspaces.setPlan.useMutation({ onSuccess: () => done('Plan updated'), onError });
  const extend = trpc.platform.workspaces.extendTrial.useMutation({ onSuccess: () => done('Trial extended'), onError });
  const suspend = trpc.platform.workspaces.suspend.useMutation({ onSuccess: () => done('Company suspended'), onError });
  const unsuspend = trpc.platform.workspaces.unsuspend.useMutation({ onSuccess: () => done('Suspension lifted'), onError });
  const setCode = trpc.platform.workspaces.setCompanyCode.useMutation({ onSuccess: () => done('Company code changed'), onError });
  const [code, setCodeValue] = useState(w.companyCode ?? '');

  const [plan, setPlanValue] = useState<'trial' | 'basic' | 'premium'>(w.plan === 'trial' ? 'premium' : w.plan);
  const [days, setDays] = useState(billing.TRIAL_DAYS);
  const [text, setText] = useState('');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Support actions</CardTitle>
        <CardDescription>Each action is recorded in this company&apos;s audit log.</CardDescription>
      </CardHeader>
      <CardFooter className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={w.stripeManaged || w.billingMode === 'hive'} onClick={() => setDialog('plan')}>
          Change plan
        </Button>
        <Button variant="outline" disabled={w.plan !== 'trial' || w.billingMode === 'hive'} onClick={() => setDialog('trial')}>
          Extend trial
        </Button>
        <Button variant="outline" onClick={() => setDialog('code')}>
          Change company code
        </Button>
        {w.suspendedAt ? (
          <Button variant="outline" loading={unsuspend.isPending} onClick={() => unsuspend.mutate({ id: w.id })}>
            Lift suspension
          </Button>
        ) : (
          <Button variant="destructive" onClick={() => setDialog('suspend')}>
            Suspend
          </Button>
        )}
      </CardFooter>

      <Dialog open={dialog !== null} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent
          title={dialog === 'plan' ? `Change ${w.name}'s plan` : dialog === 'trial' ? 'Extend the trial' : dialog === 'code' ? 'Change company code' : `Suspend ${w.name}?`}
          description={
            dialog === 'code'
              ? 'The code people type on the sign-in page. Tell the company: the old code stops working at once.'
              : dialog === 'plan'
              ? 'Comps the plan with no Stripe charge. Use this for pilots, partners and support credits.'
              : dialog === 'trial'
                ? 'Adds days to the current trial end (or to today if it already ended).'
                : 'The company becomes read-only: people can still sign in, view and export, but cannot change anything.'
          }
        >
          <div className="space-y-4">
            {dialog === 'plan' && (
              <>
                <Field label="Plan" htmlFor="pa-plan">
                  <Select
                    id="pa-plan"
                    value={plan}
                    onChange={(e) => setPlanValue(e.target.value as typeof plan)}
                    options={[
                      { value: 'premium', label: `Premium (comped, normally ${money(billing.PLANS.premium.priceCents)}/mo)` },
                      { value: 'basic', label: `Basic (comped, normally ${money(billing.PLANS.basic.priceCents)}/mo)` },
                      { value: 'trial', label: 'Restart trial' },
                    ]}
                  />
                </Field>
                {plan === 'trial' && <DaysField days={days} onChange={setDays} />}
                <Field label="Note" htmlFor="pa-note" description="Why, for the audit log.">
                  <Textarea id="pa-note" value={text} onChange={(e) => setText(e.target.value)} rows={2} />
                </Field>
                <Button loading={setPlan.isPending} onClick={() => setPlan.mutate({ id: w.id, plan, trialDays: days, note: text || undefined })}>
                  Save plan
                </Button>
              </>
            )}
            {dialog === 'code' && (
              <>
                <Field label="Company code" htmlFor="pa-code" description="3 to 12 letters or numbers.">
                  <Input id="pa-code" value={code} maxLength={12} className="uppercase" onChange={(e) => setCodeValue(e.target.value.toUpperCase())} />
                </Field>
                <Button disabled={!/^[A-Z0-9]{3,12}$/.test(code)} loading={setCode.isPending} onClick={() => setCode.mutate({ id: w.id, companyCode: code })}>
                  Save code
                </Button>
              </>
            )}
            {dialog === 'trial' && (
              <>
                <DaysField days={days} onChange={setDays} />
                <Button loading={extend.isPending} onClick={() => extend.mutate({ id: w.id, days })}>
                  Extend by {days} days
                </Button>
              </>
            )}
            {dialog === 'suspend' && (
              <>
                <Field label="Reason" htmlFor="pa-reason" required description="Shown in the company's audit log.">
                  <Textarea id="pa-reason" value={text} onChange={(e) => setText(e.target.value)} rows={2} />
                </Field>
                <Button variant="destructive" disabled={text.trim().length < 3} loading={suspend.isPending} onClick={() => suspend.mutate({ id: w.id, reason: text.trim() })}>
                  Suspend company
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DaysField({ days, onChange }: { days: number; onChange: (n: number) => void }) {
  return (
    <Field label="Days" htmlFor="pa-days">
      <Input id="pa-days" type="number" min={1} max={90} value={days} onChange={(e) => onChange(Math.max(1, Math.min(90, Number(e.target.value) || 1)))} />
    </Field>
  );
}
